import { useState, useEffect, useCallback, Fragment } from "react";
import { C, S } from "../customers/theme";
import { getCustomerScores, SEGMENTS } from "../lib/customerIntelligence";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const money = (n) => `€${(Number(n) || 0).toFixed(2)}`;
const moneyK = (n) => `€${Math.round(Number(n) || 0).toLocaleString("en-IE")}`;
const SEG_COLOR = { Champion: C.success, Loyal: C.primary, Potential: "#8b5cf6", "At Risk": C.warning, Inactive: C.textMuted };
const scoreColor = (s) => (s >= 80 ? C.success : s >= 60 ? C.primary : s >= 40 ? C.warning : C.danger);

// Plain-language "why" — describes the data layer's already-computed factors (no scoring here).
function reasonFor(r) {
  const f = r.factors || {};
  const pay = r.on_time_ratio == null ? null : (r.on_time_ratio >= 0.9 ? "pays on time" : r.on_time_ratio >= 0.5 ? "occasionally late" : "often late");
  if (r.segment === "At Risk") return `Was a ${f.M >= 4 ? "top-20%" : "valuable"} customer${r.lifetime_value ? ` (${moneyK(r.lifetime_value)} lifetime)` : ""}, no order in ${r.days_since_last_order} days.`;
  if (r.segment === "Inactive") return r.order_count === 0 ? "No orders yet." : `No recent activity — last order ${r.days_since_last_order} days ago.`;
  const bits = [`${r.order_count} order${r.order_count === 1 ? "" : "s"}`, `${moneyK(r.lifetime_value)} lifetime`];
  if (pay) bits.push(pay);
  return `${bits.join(", ")}.`;
}

function Kpi({ label, value, sub, color }) {
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderLeft: `3px solid ${color}`, borderRadius: 10, padding: "12px 16px", minWidth: 150, flex: "1 1 150px" }}>
      <div style={{ color: C.textMuted, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, marginTop: 2 }}>{value}</div>
      {sub && <div style={{ color: C.textMuted, fontSize: 12, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function Th({ label, col, sortBy, sortDir, onSort, right }) {
  const active = sortBy === col;
  return (
    <th style={{ ...S.th, textAlign: right ? "right" : "left" }} aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : "none"}>
      <button onClick={() => onSort(col)} style={{ background: "none", border: "none", color: "inherit", font: "inherit", cursor: "pointer", padding: 0, display: "inline-flex", alignItems: "center", gap: 4, textTransform: "inherit", letterSpacing: "inherit" }}>
        {label}<span style={{ opacity: active ? 1 : 0.25, fontSize: 10 }}>{active ? (sortDir === "asc" ? "▲" : "▼") : "↕"}</span>
      </button>
    </th>
  );
}

const Badge = ({ segment }) => <span style={S.pill(SEG_COLOR[segment] || C.textDim)}>{segment}</span>;

function ScoreCell({ score }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <strong style={{ minWidth: 26 }}>{score}</strong>
      <div style={{ width: 56, height: 6, background: C.surface2, borderRadius: 3, overflow: "hidden" }}>
        <div style={{ width: `${Math.max(0, Math.min(100, score))}%`, height: "100%", background: scoreColor(score) }} />
      </div>
    </div>
  );
}

const NULL_LAST = 1e12;   // sort "never ordered" (null recency) to the bottom
const SORT_VAL = {
  score: (r) => r.score,
  ltv: (r) => r.lifetime_value,
  recency: (r) => (r.days_since_last_order == null ? NULL_LAST : r.days_since_last_order),
  outstanding: (r) => r.outstanding,
};

export default function IntelligenceManager({ onOpenCustomer }) {
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [segment, setSegment] = useState("all");
  const [sortBy, setSortBy] = useState("score");
  const [sortDir, setSortDir] = useState("desc");
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try { const res = await getCustomerScores(); setData(res.data); setSummary(res.summary); }
    catch (e) { setError(e.message || "Could not load customer intelligence."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onSort = (col) => { if (sortBy === col) setSortDir((d) => (d === "asc" ? "desc" : "asc")); else { setSortBy(col); setSortDir(col === "recency" ? "asc" : "desc"); } };

  const term = search.trim().toLowerCase();
  const filtered = data
    .filter((r) => segment === "all" || r.segment === segment)
    .filter((r) => !term || `${r.company_name || ""} ${r.customer_number || ""}`.toLowerCase().includes(term));
  const sorted = [...filtered].sort((a, b) => { const va = SORT_VAL[sortBy](a), vb = SORT_VAL[sortBy](b); return (sortDir === "asc" ? 1 : -1) * (va < vb ? -1 : va > vb ? 1 : 0); });
  const top10 = [...data].sort((a, b) => b.lifetime_value - a.lifetime_value).slice(0, 10)
    .map((r) => ({ name: (r.company_name || "—").length > 18 ? `${r.company_name.slice(0, 17)}…` : (r.company_name || "—"), value: r.lifetime_value }));

  const seg = summary?.bySegment || {};

  return (
    <div>
      <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <Kpi label="Champions" value={summary?.champions ?? 0} sub="top customers" color={SEG_COLOR.Champion} />
        <Kpi label="At Risk" value={summary?.atRisk ?? 0} sub="valuable, gone quiet" color={SEG_COLOR["At Risk"]} />
        <Kpi label="Revenue from Top 20%" value={moneyK(summary?.revenueFromTop20)} sub="lifetime value" color={C.primary} />
        <Kpi label="Average Score" value={`${summary?.avgScore ?? 0}/100`} sub={`${summary?.totalCustomers ?? 0} customers`} color={C.textDim} />
      </div>

      {top10.length > 0 && (
        <div style={{ ...S.card, marginBottom: 16 }}>
          <div style={{ color: C.textMuted, fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Top 10 Customers by Lifetime Value</div>
          <ResponsiveContainer width="100%" height={Math.max(160, top10.length * 28)}>
            <BarChart data={top10} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} horizontal={false} />
              <XAxis type="number" tick={{ fill: C.textMuted, fontSize: 11 }} tickFormatter={(v) => moneyK(v)} />
              <YAxis type="category" dataKey="name" width={130} tick={{ fill: C.textMuted, fontSize: 11 }} />
              <Tooltip formatter={(v) => money(v)} contentStyle={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 12 }} labelStyle={{ color: C.text }} cursor={{ fill: C.surface2 }} />
              <Bar dataKey="value" fill={C.primary} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
        <input style={S.searchBar} placeholder="Search customers…" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search customers" />
        <div style={{ flex: 1 }} />
        <span style={{ color: C.textMuted, fontSize: 12 }}>{sorted.length} customer{sorted.length === 1 ? "" : "s"}</span>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
        {[{ value: "all", label: "All", count: summary?.totalCustomers ?? 0 }, ...SEGMENTS.map((s) => ({ value: s, label: s, count: seg[s] || 0 }))].map((chip) => {
          const active = segment === chip.value;
          const color = chip.value === "all" ? C.primary : SEG_COLOR[chip.value];
          return (
            <button key={chip.value} onClick={() => setSegment(chip.value)}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 12px", borderRadius: 999, cursor: "pointer", fontSize: 12.5, fontWeight: 600, border: `1px solid ${active ? color : C.border}`, background: active ? color : "transparent", color: active ? "#0b0f16" : C.textMuted }}>
              {chip.label}<span style={{ opacity: 0.85, fontWeight: 700 }}>{chip.count}</span>
            </button>
          );
        })}
      </div>

      {error && (
        <div role="alert" style={{ display: "flex", alignItems: "center", gap: 12, background: C.dangerDim, border: `1px solid ${C.danger}`, color: C.text, borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13 }}>
          <span style={{ color: C.danger, fontWeight: 700 }}>⚠ {error}</span>
          <button style={S.btn("secondary")} onClick={() => setError("")}>Dismiss</button>
        </div>
      )}

      <div style={S.card}>
        <table style={S.table}>
          <thead><tr>
            <th style={{ ...S.th, width: 30 }} aria-label="Expand" />
            <th style={S.th}>Customer</th>
            <Th label="Score" col="score" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
            <th style={S.th}>Segment</th>
            <Th label="Lifetime Value" col="ltv" sortBy={sortBy} sortDir={sortDir} onSort={onSort} right />
            <Th label="Last Order" col="recency" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
            <Th label="Outstanding" col="outstanding" sortBy={sortBy} sortDir={sortDir} onSort={onSort} right />
          </tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={S.empty}>Loading…</td></tr>
            ) : sorted.length === 0 ? (
              <tr><td colSpan={7} style={S.empty}>{data.length === 0 ? "No active customers to score yet." : "No customers match this filter."}</td></tr>
            ) : sorted.map((r) => (
              <Fragment key={r.id}>
                <tr style={{ cursor: "pointer" }} onClick={() => onOpenCustomer && onOpenCustomer(r.id)}>
                  <td style={S.td} onClick={(e) => { e.stopPropagation(); setExpanded(expanded === r.id ? null : r.id); }}>
                    <button aria-label={`Why ${r.company_name}`} style={{ background: "none", border: "none", color: C.textMuted, cursor: "pointer", fontSize: 12 }}>{expanded === r.id ? "▾" : "▸"}</button>
                  </td>
                  <td style={S.td}><strong>{r.company_name || "—"}</strong>{r.customer_number && <span style={{ color: C.textDim, fontSize: 11, marginLeft: 6, fontFamily: "monospace" }}>{r.customer_number}</span>}</td>
                  <td style={S.td}><ScoreCell score={r.score} /></td>
                  <td style={S.td}><Badge segment={r.segment} /></td>
                  <td style={{ ...S.td, textAlign: "right" }}>{money(r.lifetime_value)}</td>
                  <td style={S.td}>{r.days_since_last_order == null ? <span style={{ color: C.textDim }}>never</span> : `${r.days_since_last_order}d ago`}</td>
                  <td style={{ ...S.td, textAlign: "right" }}>{r.outstanding > 0 ? money(r.outstanding) : <span style={{ color: C.textDim }}>—</span>}</td>
                </tr>
                {expanded === r.id && (
                  <tr>
                    <td />
                    <td colSpan={6} style={{ ...S.td, background: C.surface2 }}>
                      <div style={{ fontSize: 13, marginBottom: 6 }}><strong style={{ color: SEG_COLOR[r.segment] }}>{r.segment}</strong> — {reasonFor(r)}</div>
                      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", fontSize: 12, color: C.textMuted }}>
                        <span>Recency <strong style={{ color: C.text }}>{r.factors.R}/5</strong></span>
                        <span>Frequency <strong style={{ color: C.text }}>{r.factors.F}/5</strong> ({r.order_count} orders)</span>
                        <span>Monetary <strong style={{ color: C.text }}>{r.factors.M}/5</strong> ({moneyK(r.lifetime_value)})</span>
                        <span>Payment <strong style={{ color: C.text }}>{r.factors.P}/100</strong>{r.on_time_ratio != null ? ` (${Math.round(r.on_time_ratio * 100)}% on time)` : " (no history)"}</span>
                        {r.avg_days_to_pay != null && <span>Avg days to pay <strong style={{ color: C.text }}>{r.avg_days_to_pay}</strong></span>}
                        {r.overdue_amount > 0 && <span style={{ color: C.warning }}>Overdue <strong>{money(r.overdue_amount)}</strong></span>}
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
